import React, { useState } from "react";
import { useNavigate, Link } from "react-router-dom";
import { toast } from "sonner";
import {
  Trash2,
  Globe,
  Sparkles,
  Info,
  FileText,
  ShieldCheck,
  UserX,
  Bot,
  ChevronRight,
} from "lucide-react";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
  AlertDialogTrigger,
} from "@/components/ui/alert-dialog";
import { Screen } from "@/components/Screen";
import { PremiumFeatureCard } from "@/components/PremiumFeatureCard";
import { FEATURES as PREMIUM_FEATURES } from "@/pages/Premium";
import { useI18n } from "@/i18n/I18nProvider";
import { clearAllLocalData } from "@/store/store";

export default function Settings() {
  const { t, language, setLanguage } = useI18n();
  const navigate = useNavigate();
  const [confirmOpen, setConfirmOpen] = useState(false);

  const handleDeleteData = () => {
    clearAllLocalData();
    setConfirmOpen(false);
    toast.success(t("settings.data.toastDeleted"));
    // Small delay so the toast is visible before the redirect.
    setTimeout(() => navigate("/", { replace: true }), 350);
  };

  return (
    <Screen testId="settings-screen">
      <header className="pt-4">
        <p className="text-xs uppercase tracking-[0.18em] text-[#E8D8B8]/70">
          {t("app.tagline")}
        </p>
        <h1
          className="mt-2 text-3xl sm:text-4xl font-medium leading-tight"
          style={{ letterSpacing: "-0.02em" }}
          data-testid="settings-title"
        >
          {t("settings.title")}
        </h1>
        <p className="mt-2 text-[15px] text-[#CBD5E1]">
          {t("settings.subtitle")}
        </p>
      </header>

      {/* Language */}
      <section className="mt-8">
        <SectionHeader
          icon={Globe}
          title={t("settings.language.heading")}
          description={t("settings.language.description")}
        />
        <div
          className="dc-card mt-3 p-1.5 grid grid-cols-2 gap-1.5"
          data-testid="settings-language-toggle"
        >
          <LangOption
            code="pl"
            label="Polski"
            active={language === "pl"}
            onClick={() => setLanguage("pl")}
          />
          <LangOption
            code="en"
            label="English"
            active={language === "en"}
            onClick={() => setLanguage("en")}
          />
        </div>
      </section>

      {/* Premium */}
      <section className="mt-8">
        <div
          className="dc-card relative overflow-hidden p-5"
          data-testid="settings-premium-card"
        >
          <div
            className="absolute -top-14 -right-14 w-40 h-40 rounded-full pointer-events-none"
            style={{
              background:
                "radial-gradient(circle, rgba(244,201,93,0.22) 0%, rgba(244,201,93,0) 70%)",
            }}
          />
          <div className="flex items-start gap-3 relative">
            <span
              className="flex-none w-10 h-10 rounded-xl flex items-center justify-center"
              style={{
                background:
                  "linear-gradient(135deg, rgba(244,201,93,0.22), rgba(167,139,250,0.18))",
              }}
            >
              <Sparkles size={18} className="text-[#F4C95D]" strokeWidth={1.6} />
            </span>
            <div className="flex-1">
              <h2
                className="text-lg font-medium"
                style={{ letterSpacing: "-0.015em" }}
              >
                {t("premium.settingsCard.heading")}
              </h2>
              <p className="mt-1.5 text-sm text-[#CBD5E1] leading-relaxed">
                {t("premium.settingsCard.subtitle")}
              </p>
            </div>
          </div>

          <p className="relative mt-4 text-[13px] text-[#CBD5E1] leading-relaxed">
            {t("premium.hero.description.p1")}
          </p>
          <p className="relative mt-3 text-[13px] text-[#CBD5E1] leading-relaxed">
            {t("premium.hero.description.p2")}
          </p>

          <div
            className="relative mt-5 grid grid-cols-2 sm:grid-cols-3 gap-2"
            data-testid="settings-premium-features"
          >
            {PREMIUM_FEATURES.map(({ key, Icon }) => (
              <PremiumFeatureCard
                key={key}
                Icon={Icon}
                titleKey={`premium.features.${key}.title`}
                variant="compact"
                testId={`settings-premium-feature-${key}`}
              />
            ))}
          </div>

          <button
            type="button"
            onClick={() => navigate("/premium")}
            data-testid="settings-premium-cta"
            className="group relative mt-6 w-full inline-flex items-center justify-center gap-2 rounded-full px-5 py-3 text-sm font-medium text-white/85 border border-white/12 hover:bg-white/[0.05] hover:text-white transition-colors duration-200"
          >
            {t("premium.cta.seeIncludes")}
            <ChevronRight
              size={15}
              strokeWidth={1.7}
              className="transition-transform duration-300 group-hover:translate-x-0.5"
            />
          </button>
        </div>
      </section>

      {/* About */}
      <section className="mt-8">
        <SectionHeader
          icon={Info}
          title={t("settings.about.heading")}
        />
        <div className="dc-card mt-3 p-5" data-testid="settings-about-card">
          <p className="text-sm text-[#CBD5E1] leading-relaxed">
            {t("settings.about.description")}
          </p>
          <p className="mt-3 text-xs text-[#E8D8B8]/70 tracking-wide">
            {t("settings.about.version")}
          </p>
        </div>
      </section>

      {/* Legal */}
      <section className="mt-8">
        <SectionHeader
          icon={FileText}
          title={t("settings.legal.heading")}
        />
        <div className="dc-card mt-3 divide-y divide-white/[0.06] overflow-hidden">
          <LegalLink
            to="/privacy"
            icon={ShieldCheck}
            label={t("settings.legal.privacy")}
            testId="legal-link-privacy"
          />
          <LegalLink
            to="/terms"
            icon={FileText}
            label={t("settings.legal.terms")}
            testId="legal-link-terms"
          />
          <LegalLink
            to="/delete-account"
            icon={UserX}
            label={t("settings.legal.deleteData")}
            testId="legal-link-delete-data"
          />
          <LegalLink
            to="/ai-notice"
            icon={Bot}
            label={t("settings.legal.aiNotice")}
            testId="legal-link-ai-notice"
          />
        </div>
      </section>

      {/* Data */}
      <section className="mt-8">
        <SectionHeader
          icon={Trash2}
          title={t("settings.data.heading")}
          description={t("settings.data.description")}
        />
        <div className="mt-3">
          <AlertDialog open={confirmOpen} onOpenChange={setConfirmOpen}>
            <AlertDialogTrigger asChild>
              <button
                type="button"
                data-testid="settings-delete-data-btn"
                className="w-full inline-flex items-center justify-center gap-2 rounded-full px-5 py-3.5 text-sm font-medium transition-colors duration-200 border border-red-400/30 text-red-300 hover:bg-red-400/[0.08] hover:text-red-200"
              >
                <Trash2 size={16} strokeWidth={1.8} />
                {t("settings.data.deleteButton")}
              </button>
            </AlertDialogTrigger>
            <AlertDialogContent
              className="dc-card border-0 max-w-[380px]"
              style={{
                background: "rgba(15,23,42,0.96)",
              }}
            >
              <AlertDialogHeader>
                <AlertDialogTitle
                  className="text-[#F8FAFC]"
                  style={{ letterSpacing: "-0.015em" }}
                >
                  {t("settings.data.confirmTitle")}
                </AlertDialogTitle>
                <AlertDialogDescription className="text-[#CBD5E1]">
                  {t("settings.data.confirmDescription")}
                </AlertDialogDescription>
              </AlertDialogHeader>
              <AlertDialogFooter className="mt-2 gap-2">
                <AlertDialogCancel
                  data-testid="settings-delete-cancel"
                  className="rounded-full border-white/10 bg-white/[0.04] text-white hover:bg-white/[0.08] hover:text-white"
                >
                  {t("common.cancel")}
                </AlertDialogCancel>
                <AlertDialogAction
                  onClick={handleDeleteData}
                  data-testid="settings-delete-confirm"
                  className="rounded-full bg-red-500/90 text-white hover:bg-red-500"
                >
                  {t("settings.data.confirmAction")}
                </AlertDialogAction>
              </AlertDialogFooter>
            </AlertDialogContent>
          </AlertDialog>
        </div>
      </section>
    </Screen>
  );
}

function SectionHeader({ icon: Icon, title, description }) {
  return (
    <div className="flex items-start gap-2">
      <Icon size={16} className="mt-0.5 text-[#F4C95D]/80" strokeWidth={1.7} />
      <div>
        <h2
          className="text-sm uppercase tracking-[0.14em] text-[#E8D8B8]/85"
          style={{ letterSpacing: "0.14em" }}
        >
          {title}
        </h2>
        {description && (
          <p className="mt-1 text-[13px] text-[#94A3B8]">{description}</p>
        )}
      </div>
    </div>
  );
}

function LangOption({ code, label, active, onClick }) {
  return (
    <button
      type="button"
      onClick={onClick}
      data-testid={`settings-language-${code}`}
      aria-pressed={active}
      className={`px-4 py-2.5 rounded-2xl text-sm font-medium transition-all duration-200 ${
        active
          ? "bg-[#F4C95D] text-[#0F172A] shadow-[0_6px_20px_-10px_rgba(244,201,93,0.7)]"
          : "text-white/70 hover:text-white hover:bg-white/[0.04]"
      }`}
    >
      {label}
    </button>
  );
}

function LegalLink({ to, icon: Icon, label, testId }) {
  return (
    <Link
      to={to}
      data-testid={testId}
      className="flex items-center gap-3 px-4 py-3.5 group transition-colors duration-200 hover:bg-white/[0.03]"
    >
      <Icon size={16} className="text-[#CBD5E1]" strokeWidth={1.6} />
      <span className="flex-1 text-[14px] text-[#F8FAFC]/90">{label}</span>
      <ChevronRight
        size={16}
        className="text-white/30 transition-transform duration-200 group-hover:translate-x-0.5"
        strokeWidth={1.6}
      />
    </Link>
  );
}
